import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSports } from '@/lib/api/queries/useSports'
import { sportsRepository } from '@/lib/api/repositories/sportsRepository'
import { isSportEligible } from '@/lib/utils/sportFilters'
import { Trophy, Check, AlertCircle, Dumbbell } from 'lucide-react'
import type { UseFormSetValue, UseFormWatch, FieldErrors } from 'react-hook-form'
import type { RegistrationFormData } from '@/lib/types/registration'
import type { Sport } from '@/lib/types/sport'

// Get sport icon based on display text (similar to Flutter's approach)
function getSportIcon(displayText: string): string {
  const name = displayText.toLowerCase()

  if (name.includes('cricket')) return '🏏'
  if (name.includes('football')) return '⚽'
  if (name.includes('volleyball')) return '🏐'
  if (name.includes('badminton')) return '🏸'
  if (name.includes('table tennis')) return '🏓'
  if (name.includes('tennis')) return '🎾'
  if (name.includes('basketball')) return '🏀'
  if (name.includes('carrom')) return '🎯'
  if (name.includes('chess')) return '♟️'
  if (name.includes('pickleball')) return '🎾'

  return '🏅' // Default fallback
}

interface SportsSelectionSectionProps {
  setValue: UseFormSetValue<RegistrationFormData>
  watch: UseFormWatch<RegistrationFormData>
  errors: FieldErrors<RegistrationFormData>
}

export function SportsSelectionSection({ setValue, watch, errors }: SportsSelectionSectionProps) {
  const { data: sports, isLoading } = useSports()
  const selectedGender = watch('gender')
  const selectedDob = watch('dob')
  const selectedSportIds = watch('selectedSportIds') || []

  const groupedSports = useMemo(() => {
    if (!sports) return {}
    return sportsRepository.groupSportsByCategory(sports)
  }, [sports])

  const filteredGroupedSports = useMemo(() => {
    if (!selectedGender || !selectedDob) return groupedSports

    const filtered: Record<string, Sport[]> = {}
    Object.entries(groupedSports).forEach(([category, sportsList]) => {
      const eligible = sportsList.filter((sport) =>
        isSportEligible(sport, selectedGender, selectedDob)
      )
      if (eligible.length > 0) {
        filtered[category] = eligible
      }
    })
    return filtered
  }, [groupedSports, selectedGender, selectedDob])

  const toggleSport = (sportId: number) => {
    const newSelection = selectedSportIds.includes(sportId)
      ? selectedSportIds.filter((id) => id !== sportId)
      : [...selectedSportIds, sportId]
    setValue('selectedSportIds', newSelection)
  }

  if (isLoading) {
    return (
      <Card variant="premium">
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center animate-pulse">
              <Trophy className="w-6 h-6 text-primary/50" />
            </div>
            <p className="text-slate-500">Loading sports...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!selectedGender || !selectedDob) {
    return (
      <Card variant="premium">
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900 mb-1">Complete Your Profile</p>
              <p className="text-sm text-slate-500 max-w-sm">
                Please select your gender and date of birth to see available sports
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="premium">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle>Select Sports</CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">
              Choose the sports you want to participate in
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                Sports are filtered based on your gender and age. Select all sports you're interested in.
              </span>
            </p>
          </div>

          {Object.entries(filteredGroupedSports).length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">No sports available</p>
              <p className="text-sm text-slate-400 mt-1">
                No sports match your age and gender criteria
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(filteredGroupedSports).map(([category, sportsList]) => (
                <div key={category}>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <span className="w-6 h-px bg-slate-300" />
                    {category}
                    <span className="w-6 h-px bg-slate-300" />
                  </h4>
                  {/* Grid layout for sports cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {sportsList.map((sport) => {
                      const isSelected = selectedSportIds.includes(sport.id)
                      return (
                        <button
                          key={sport.id}
                          type="button"
                          onClick={() => toggleSport(sport.id)}
                          className={`
                            p-4 rounded-xl border-2 text-left transition-all duration-200
                            ${isSelected
                              ? 'border-primary bg-primary/5 shadow-md'
                              : 'border-slate-200 bg-white hover:border-primary/30 hover:shadow-sm'
                            }
                          `}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-2xl">{getSportIcon(sport.display_text)}</span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="font-medium text-sm text-slate-900 leading-tight">
                            {sport.display_text}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {errors.selectedSportIds && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.selectedSportIds.message}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
